import React from 'react';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle.js';
import { useContent } from '../../../contexts/ContentContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useGameType } from '../../../contexts/GameTypeContext';
import { useSiteConfig } from '../../../contexts/SiteConfigContext';
import { useGeoLocation } from '../../../contexts/GeoLocationContext';
import { getLegalAgeNumber, interpolateLegalAge } from '../../../utils/legalAge';
import {
  HowToPlayText,
  HowToPlayTextClassic,
  HowToPlayTextCTA,
  HowToPlayAlertTitle,
  HowToPlayAlertAge,
  HowToPlayAlertContact,
  HowToPlayAlertNotify,
  HowToPlayAlertFinal,
  HowToPlayAlertResponsible,
} from '../../../content/keys';

const FALLBACKS = {
  [HowToPlayAlertTitle]: 'Important Information',
  [HowToPlayAlertAge]: 'You must be 18 or older to play',
  [HowToPlayAlertContact]: 'Keep your contact information up to date',
  [HowToPlayAlertNotify]: 'Enable notifications to never miss a draw',
  [HowToPlayAlertFinal]: 'All ticket sales are final',
  [HowToPlayAlertResponsible]: 'Please play responsibly',
};

export default function HowToGameRulesSection({ section }) {
  const { c } = useContent();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { t: tr } = useTranslations();
  const { gameType } = useGameType();
  const { siteDetails } = useSiteConfig();
  const { locationData } = useGeoLocation();

  const legalAge = getLegalAgeNumber(siteDetails, locationData);

  const t = (key) => {
    const v = c(key);
    if (v) return interpolateLegalAge(v, legalAge);
    const trVal = tr(key);
    if (trVal && trVal !== key) return interpolateLegalAge(trVal, legalAge);
    if (key === HowToPlayAlertAge) return `You must be ${legalAge} or older to play`;
    return FALLBACKS[key] || '';
  };

  const rulesKey = gameType === 'chaseTheAce'
    ? HowToPlayTextCTA
    : gameType === 'modified5050'
      ? HowToPlayText
      : HowToPlayTextClassic;

  const customRulesText = c(rulesKey, '') || c(HowToPlayText, '') || (gameType === 'chaseTheAce' ? c(HowToPlayTextClassic, '') : '');

  const alertItems = [
    HowToPlayAlertAge,
    HowToPlayAlertContact,
    HowToPlayAlertNotify,
    HowToPlayAlertFinal,
    HowToPlayAlertResponsible
  ];

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-l-4 border-amber-500 rounded-lg p-3.5 sm:p-6 mb-6 sm:mb-12">
      <div className="flex items-start gap-2.5 sm:gap-4">
        <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <InlineTranslationEdit
            translationKey={HowToPlayAlertTitle}
            value={t(HowToPlayAlertTitle)}
            fallback={FALLBACKS[HowToPlayAlertTitle]}
            editMode={editMode}
            onUpdate={handleTranslationUpdate}
            className="text-sm sm:text-lg font-bold text-amber-900 dark:text-amber-300 mb-1.5 sm:mb-2 block"
          />
          <ul className="space-y-1 sm:space-y-2 text-amber-800 dark:text-amber-400 text-xs sm:text-sm">
            {alertItems.map((itemKey, index) => (
              <li key={index} className="flex items-start gap-1">
                <span>•</span>
                <InlineTranslationEdit
                  translationKey={itemKey}
                  value={t(itemKey)}
                  fallback={itemKey === HowToPlayAlertAge ? `You must be ${legalAge} or older to play` : FALLBACKS[itemKey]}
                  editMode={editMode}
                  onUpdate={handleTranslationUpdate}
                />
              </li>
            ))}
          </ul>
          {(customRulesText || editMode) && (
            <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-800/50">
              <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
                {tr('howToPlay.rulesOfPlay') !== 'howToPlay.rulesOfPlay' ? tr('howToPlay.rulesOfPlay') : 'Rules of Play'}
              </h4>
              <InlineTranslationEdit
                translationKey={rulesKey}
                value={customRulesText}
                fallback=""
                defaultText="Click to add custom rules of play..."
                multiline
                editMode={editMode}
                onUpdate={handleTranslationUpdate}
                className="text-xs sm:text-sm text-amber-900 dark:text-amber-300 leading-relaxed block"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
